/**
 * Widget styles. Injected into a closed Shadow DOM root so they cannot leak
 * into — or be polluted by — the host page's CSS.
 *
 * Klientele design system (FR-2026-0105, Direction A "Composer"):
 *   - Geist / Geist Mono (registered by fonts.ts), the neutral scale;
 *   - 6px radius on controls, 8px on the composer box, 12px on the panel;
 *   - the firm's accent ({{ACCENT}}) used once, on Voice;
 *   - the launcher takes {{COLOR}} (data-color, else the firm's accent).
 *
 * The {{COLOR}} / {{ACCENT}} / {{ACCENT_SOFT}} tokens are replaced at runtime.
 */

export const STYLES = `
:host {
  --df-color: {{COLOR}};
  --df-color-fg: #ffffff;
  --df-accent: {{ACCENT}};
  --df-accent-soft: {{ACCENT_SOFT}};
  --df-canvas: #FAFAF9;
  --df-bg: #FFFFFF;
  --df-bg-soft: #F5F5F4;
  --df-invert: #0A0A0A;
  --df-text: #0A0A0A;
  --df-text-secondary: #404040;
  --df-muted: #737373;
  --df-placeholder: #A3A3A3;
  --df-border: #D4D4D4;
  --df-border-soft: #E5E5E5;
  --df-error: #DC2626;
  --df-error-bg: #FEF2F2;
  --df-success: #16A34A;
  --df-mono: 'Klientele Geist Mono', 'Geist Mono', ui-monospace, Menlo, Consolas, monospace;
  --df-font: 'Klientele Geist', 'Geist', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* The host element carries \`all: initial\` (so the page's CSS cannot reach
   it), which also resets inherited font and colour on :host. Custom
   properties survive \`all\`, so every top-level surface sets its own. */
.df-launcher, .df-backdrop, .df-rec-pill, .df-annot-overlay, .df-player-overlay {
  font-family: var(--df-font);
  color: var(--df-text);
  -webkit-font-smoothing: antialiased;
  text-align: left;
}

* { box-sizing: border-box; }
button { font-family: inherit; }

/* ============================================================
   Launcher — the floating button on the host page
   ============================================================ */

.df-launcher {
  position: fixed;
  z-index: 2147483640;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  /* Launcher takes the workspace's configured brand color via data-color. */
  background: var(--df-color);
  color: var(--df-color-fg);
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.005em;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0,0,0,0.14), 0 2px 4px rgba(0,0,0,0.06);
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
}
.df-launcher:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,0.18); filter: brightness(0.96); }
.df-launcher:active { transform: translateY(0); }
.df-launcher:focus-visible { outline: 2px solid var(--df-color-fg); outline-offset: 3px; }

/* Icon-only round launcher (data-launcher="icon"). Doubled class so it also
   wins over the narrow-screen padding rule further down. */
.df-launcher.df-launcher-icon {
  width: 56px;
  height: 56px;
  padding: 0;
  gap: 0;
  justify-content: center;
  border-radius: 9999px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.08);
}
.df-launcher.df-launcher-icon:hover { transform: scale(1.05); }
.df-launcher.df-launcher-icon .df-label-text { display: none; }
.df-launcher.df-launcher-icon .df-icon svg,
.df-launcher.df-launcher-icon .df-icon img { width: 40px !important; height: 40px !important; }

.df-pos-bottom-right { right: 24px; bottom: 24px; }
.df-pos-bottom-left  { left: 24px; bottom: 24px; }
.df-pos-top-right    { right: 24px; top: 24px; }
.df-pos-top-left     { left: 24px; top: 24px; }

.df-icon { display: inline-flex; align-items: center; justify-content: center; line-height: 1; }
.df-icon svg, .df-icon img { display: block; }
.df-hidden { display: none !important; }


/* ============================================================
   Panel (FR-2026-0105, Direction A "Composer"). Anchored to the
   launcher's corner on desktop; a bottom sheet on phones.
   ============================================================ */

.df-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.18);
  z-index: 2147483641;
  display: flex;
  padding: 20px;
  animation: df-fade 160ms ease-out;
}
.df-backdrop.df-at-bottom-right { align-items: flex-end; justify-content: flex-end; }
.df-backdrop.df-at-bottom-left  { align-items: flex-end; justify-content: flex-start; }
.df-backdrop.df-at-top-right    { align-items: flex-start; justify-content: flex-end; }
.df-backdrop.df-at-top-left     { align-items: flex-start; justify-content: flex-start; }

.df-modal {
  position: relative;
  background: var(--df-bg);
  border: 1px solid var(--df-border-soft);
  border-radius: 12px;
  width: 400px;
  max-width: 100%;
  max-height: calc(100vh - 40px);
  max-height: calc(100dvh - 40px);
  max-height: calc(var(--df-vvh, 100dvh) - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px -12px rgba(10,10,10,.18), 0 4px 12px -4px rgba(10,10,10,.06);
  animation: df-rise 200ms cubic-bezier(.2,.7,.3,1.05);
  font-size: 13.5px;
  line-height: 1.5;
}
.df-modal:focus { outline: none; }
.df-modal-scroll { overflow-y: auto; flex: 1 1 auto; min-height: 0; }
.df-grab { display: none; width: 36px; height: 4px; border-radius: 2px; background: var(--df-border); margin: 8px auto 0; flex: none; }

.df-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }

/* Header: firm logo + "Tell <firm>" + one-line expectation */
.df-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid var(--df-border-soft);
  flex: none;
}
.df-logo {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--df-invert);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
  flex: none;
  overflow: hidden;
  letter-spacing: 0;
}
.df-logo.df-logo-img { background: var(--df-bg); border: 1px solid var(--df-border-soft); }
.df-logo img { width: 100%; height: 100%; object-fit: contain; display: block; }
.df-head-text { flex: 1; min-width: 0; }
.df-title {
  display: block;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--df-text);
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.df-subtitle { display: block; font-size: 12px; color: var(--df-muted); line-height: 1.4; }
.df-close {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 0;
  background: transparent;
  color: var(--df-muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  flex: none;
  padding: 0;
}
.df-close:hover { background: var(--df-bg-soft); color: var(--df-text); }

/* Focus ring shared by every control */
.df-modal button:focus-visible,
.df-modal a:focus-visible,
.df-modal [role="radio"]:focus-visible,
.df-input:focus-visible {
  outline: 2px solid var(--df-text);
  outline-offset: 2px;
}

/* Body */
.df-form { padding: 14px 16px; display: flex; flex-direction: column; gap: 12px; }
.df-lbl {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--df-text);
  margin: 0 0 6px;
}
.df-lbl em { font-style: normal; font-weight: 400; color: var(--df-muted); }

/* The composer box */
.df-composer {
  border: 1px solid var(--df-border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--df-bg);
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.df-composer:focus-within { border-color: var(--df-text); box-shadow: 0 0 0 1px var(--df-text); }
.df-subj, .df-body {
  display: block;
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  color: var(--df-text);
  margin: 0;
}
.df-subj { padding: 10px 12px 4px; font-size: 14px; font-weight: 500; }
.df-body {
  padding: 2px 12px 10px;
  font-size: 13.5px;
  line-height: 1.5;
  min-height: 70px;
  max-height: 180px;
  resize: none;
  color: var(--df-text-secondary);
}
.df-subj::placeholder, .df-body::placeholder, .df-input::placeholder { color: var(--df-placeholder); opacity: 1; }
.df-evidence { display: flex; flex-direction: column; gap: 8px; padding: 0 12px 10px; }
.df-evidence:empty { display: none; }
.df-thumbs { display: flex; gap: 8px; flex-wrap: wrap; }
.df-thumbs:empty { display: none; }

.df-thumb {
  position: relative;
  width: 64px;
  height: 48px;
  border-radius: 6px;
  border: 1px solid var(--df-border-soft);
  background: linear-gradient(135deg, #f5f5f4, #e7e5e4);
  padding: 0;
  overflow: hidden;
  display: grid;
  place-items: center;
  color: var(--df-muted);
  cursor: default;
  font: inherit;
}
.df-thumb.df-thumb-open { cursor: pointer; }
.df-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.df-thumb-name {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  font-size: 9.5px;
  line-height: 1.3;
  padding: 1px 4px;
  background: rgba(255,255,255,.88);
  color: var(--df-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}
.df-thumb-x {
  position: absolute;
  right: 3px;
  top: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid var(--df-border);
  color: var(--df-muted);
  display: grid;
  place-items: center;
  padding: 0;
  cursor: pointer;
}
.df-thumb-x:hover { color: var(--df-text); border-color: var(--df-text); }
.df-thumb-x svg { width: 9px; height: 9px; }
.df-thumb.df-failed { border-color: var(--df-error); }
.df-thumb.df-busy::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,.55);
}

/* Voice note row + live recording row */
.df-voice, .df-rec {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  border-radius: 8px;
  padding: 0 6px 0 10px;
}
.df-voice { border: 1px solid var(--df-border-soft); background: var(--df-bg-soft); }
.df-voice.df-failed { border-color: var(--df-error); }
.df-rec { border: 1px solid var(--df-error); background: var(--df-error-bg); }
.df-play {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--df-invert);
  color: #fff;
  border: 0;
  display: grid;
  place-items: center;
  padding: 0;
  cursor: pointer;
  flex: none;
}
.df-bars { flex: 1; display: flex; gap: 2px; align-items: center; height: 22px; min-width: 0; }
.df-bars b { flex: 1; min-width: 1px; background: var(--df-muted); border-radius: 1px; transition: height 80ms linear; }
.df-voice.df-playing .df-bars b.df-played { background: var(--df-text); }
.df-rec .df-bars b { background: var(--df-error); opacity: .7; }
.df-tm { font-family: var(--df-mono); font-size: 11px; font-weight: 500; color: var(--df-muted); font-variant-numeric: tabular-nums; white-space: nowrap; }
.df-rec .df-tm { color: var(--df-error); }
.df-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 0;
  background: transparent;
  color: var(--df-muted);
  display: grid;
  place-items: center;
  padding: 0;
  cursor: pointer;
  flex: none;
}
.df-icon-btn:hover { background: rgba(10,10,10,.06); color: var(--df-text); }
.df-rec-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--df-error); flex: none; animation: df-pulse 1s ease-in-out infinite; }
.df-stop {
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: var(--df-error);
  color: #fff;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 0 10px;
  cursor: pointer;
  flex: none;
}
.df-stop:hover { filter: brightness(.94); }

/* Composer toolbar */
.df-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid var(--df-border-soft);
  padding: 4px 6px;
  background: var(--df-canvas);
}
.df-tools { display: flex; gap: 2px; align-items: center; }
.df-tool {
  height: 32px;
  min-width: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--df-text-secondary);
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
}
.df-tool:hover:not(:disabled) { background: rgba(10,10,10,.05); color: var(--df-text); }
.df-tool:disabled { opacity: .45; cursor: not-allowed; }
.df-tool svg { display: block; flex: none; }
.df-tool.df-mic { color: var(--df-accent); }
.df-tool.df-mic:hover:not(:disabled) { color: var(--df-accent); background: var(--df-accent-soft); }
.df-count { font-size: 11.5px; color: var(--df-muted); font-variant-numeric: tabular-nums; white-space: nowrap; padding-right: 4px; }
.df-count.df-low { color: var(--df-error); }

/* Urgency chips (radio group) */
.df-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.df-chip {
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 10px;
  border: 1px solid var(--df-border);
  border-radius: 999px;
  background: var(--df-bg);
  color: var(--df-text-secondary);
  cursor: pointer;
  line-height: 1.3;
}
.df-chip:hover { border-color: var(--df-text-secondary); color: var(--df-text); }
.df-chip[aria-checked="true"] { border-color: var(--df-text); color: var(--df-text); box-shadow: inset 0 0 0 1px var(--df-text); }

/* Email + Send */
.df-send-row { display: flex; gap: 8px; align-items: flex-end; }
.df-send-row > .df-field { flex: 1; min-width: 0; }
.df-input {
  display: block;
  width: 100%;
  height: 36px;
  border: 1px solid var(--df-border);
  border-radius: 6px;
  padding: 0 10px;
  font: inherit;
  font-size: 13.5px;
  color: var(--df-text);
  background: var(--df-bg);
  outline: none;
}
.df-input:focus { border-color: var(--df-text); box-shadow: 0 0 0 1px var(--df-text); }
.df-input[aria-invalid="true"] { border-color: var(--df-error); }
.df-send {
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: var(--df-invert);
  color: #fff;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
  flex: none;
}
.df-send:hover:not(:disabled) { background: #262626; }
.df-send:disabled { opacity: .6; cursor: progress; }
.df-send.df-full { width: 100%; }
.df-send.df-ghost { background: var(--df-bg); color: var(--df-text); border: 1px solid var(--df-border); }
.df-send.df-ghost:hover { background: var(--df-bg-soft); }

/* Inline message (errors, mic denied, unsupported) */
.df-msg {
  font-size: 12.5px;
  line-height: 1.45;
  padding: 8px 10px;
  border-radius: 6px;
  background: var(--df-error-bg);
  color: var(--df-error);
  border: 1px solid rgba(220,38,38,.25);
}

/* Footer */
.df-footer {
  padding: 10px 16px;
  border-top: 1px solid var(--df-border-soft);
  font-size: 10.5px;
  white-space: nowrap;
  color: var(--df-placeholder);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex: none;
}
.df-footer > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.df-footer a { color: inherit; text-decoration: none; white-space: nowrap; flex: none; }
.df-footer a:hover { color: var(--df-text-secondary); text-decoration: underline; }
.df-foot-short { display: none; }

/* After sending */
.df-done { padding: 22px 16px 16px; text-align: center; }
.df-tick {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #F0FDF4;
  color: var(--df-success);
  display: grid;
  place-items: center;
  margin: 0 auto 10px;
}
.df-done-title { display: block; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; margin: 0; color: var(--df-text); outline: none; }
.df-done p { margin: 6px 0 0; font-size: 13.5px; color: var(--df-text-secondary); }
.df-done .df-note { font-size: 11.5px; color: var(--df-muted); margin: 0 0 12px; }
.df-done .df-warn { color: var(--df-error); font-size: 12px; }
.df-ref {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
  padding: 4px 4px 4px 10px;
  border: 1px solid var(--df-border-soft);
  border-radius: 6px;
  background: var(--df-canvas);
  text-align: left;
}
.df-ref-label { display: block; font-size: 10.5px; color: var(--df-muted); font-weight: 500; }
.df-ref-num { font-family: var(--df-mono); font-size: 13px; font-weight: 600; color: var(--df-text); letter-spacing: .01em; }
.df-copy {
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--df-muted);
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 0 10px;
  cursor: pointer;
}
.df-copy:hover { background: rgba(10,10,10,.05); color: var(--df-text); }

.df-spinner { animation: df-spin 700ms linear infinite; transform-origin: 50% 50%; }
@keyframes df-spin { to { transform: rotate(360deg); } }


/* ============================================================
   External recording pill — floats on the page while the modal
   is hidden during an active screen recording. Lives in the
   shadow root (position:fixed inside shadow DOM works the same
   as in the main document).
   ============================================================ */

.df-rec-pill {
  position: fixed;
  bottom: 24px;
  left: 24px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(10, 10, 10, 0.92);
  border: 1px solid rgba(220, 38, 38, 0.5);
  border-radius: 999px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.005em;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
  z-index: 2147483641;
  white-space: nowrap;
}
.df-rec-pill-timer { font-variant-numeric: tabular-nums; color: #ffffff; }
.df-rec-pill-stop {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--df-error);
  color: #ffffff;
  border: none;
  border-radius: 999px;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
  transition: filter 100ms ease;
}
.df-rec-pill-stop:hover { filter: brightness(0.92); }
.df-rec-pill-stop svg { display: block; }


/* ============================================================
   Annotator — full-screen screenshot editor
   ============================================================ */

.df-annot-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.78);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 2147483646;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: df-fade 160ms ease-out;
}
.df-annot-frame {
  background: var(--df-bg);
  border: 1px solid var(--df-border-soft);
  border-radius: 12px;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0,0,0,0.32);
}
.df-annot-header {
  background: var(--df-bg);
  color: var(--df-text);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--df-border-soft);
}
.df-annot-title {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.005em;
  flex: 1;
  line-height: 1.2;
  color: var(--df-text);
}
.df-annot-close {
  background: transparent;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--df-placeholder);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 100ms ease, color 100ms ease;
}
.df-annot-close:hover { background: var(--df-bg-soft); color: var(--df-text); }
.df-annot-close svg { display: block; }

.df-annot-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--df-canvas);
  border-bottom: 1px solid var(--df-border-soft);
  flex-shrink: 0;
  flex-wrap: wrap;
}
.df-annot-tools, .df-annot-colors {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.df-annot-divider {
  width: 1px;
  align-self: stretch;
  background: var(--df-border-soft);
  margin: 2px 4px;
}
.df-annot-spacer { flex: 1; }

.df-annot-tool, .df-annot-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--df-bg);
  color: var(--df-text-secondary);
  border: 1px solid var(--df-border-soft);
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
}
.df-annot-tool { padding: 6px; }
.df-annot-tool:hover, .df-annot-action:hover { background: var(--df-bg-soft); color: var(--df-text); border-color: var(--df-border); }
.df-annot-tool.df-annot-active {
  background: color-mix(in srgb, var(--df-color) 10%, transparent);
  color: var(--df-color);
  border-color: color-mix(in srgb, var(--df-color) 45%, transparent);
}
.df-annot-tool svg, .df-annot-action svg { display: block; }

.df-annot-color {
  width: 24px;
  height: 24px;
  border: 1px solid var(--df-border-soft);
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: transform 100ms ease;
}
.df-annot-color:hover { transform: scale(1.08); }
.df-annot-color.df-annot-active {
  box-shadow: 0 0 0 2px var(--df-bg), 0 0 0 4px var(--df-color);
}

.df-annot-cancel {
  padding: 7px 12px;
  background: var(--df-bg);
  color: var(--df-text-secondary);
  border: 1px solid var(--df-border-soft);
  border-radius: 6px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
}
.df-annot-cancel:hover { background: var(--df-bg-soft); color: var(--df-text); border-color: var(--df-border); }

.df-annot-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: var(--df-color);
  color: var(--df-color-fg);
  border: 1px solid var(--df-color);
  border-radius: 6px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: filter 100ms ease;
}
.df-annot-save:hover { filter: brightness(0.95); }
.df-annot-save svg { display: block; }

.df-annot-canvas-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: repeating-conic-gradient(#E5E5E5 0% 25%, #F5F5F5 0% 50%) 50% / 24px 24px;
  overflow: auto;
  padding: 16px;
  position: relative;
}
.df-annot-canvas {
  display: block;
  background: #ffffff;
  border-radius: 6px;
  cursor: crosshair;
  touch-action: none;
  box-shadow: 0 6px 24px rgba(0,0,0,0.18);
}

/* Floating multi-line text input shown when user clicks with the Text tool.
   Positioned in CSS px relative to the canvas-wrap; the typed value is
   rendered into the canvas at canvas-buffer coordinates on commit.
   Cmd/Ctrl+Enter to save, Esc to cancel, plain Enter inserts a newline. */
.df-annot-text-input {
  position: absolute;
  z-index: 10;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid var(--df-color);
  border-radius: 4px;
  padding: 4px 6px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 500;
  outline: none;
  resize: none;
  overflow: hidden;
  white-space: pre;
  min-width: 80px;
  min-height: 32px;
  max-width: 100%;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--df-color) 18%, transparent), 0 4px 12px rgba(0,0,0,0.18);
}
.df-annot-text-input::placeholder {
  color: var(--df-placeholder);
  font-weight: 400;
  font-style: italic;
}

@media (max-width: 600px) {
  .df-annot-overlay { padding: 0; }
  .df-annot-frame { border: 0; border-radius: 0; max-height: 100%; }
  .df-annot-toolbar { gap: 4px; padding: 6px 8px; }
  .df-annot-action span, .df-annot-save span { display: none; }
}

/* ============================================================
   Video player — fullscreen preview for queued recordings
   ============================================================ */

.df-player-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 2147483646;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: df-fade 160ms ease-out;
}
.df-player-frame {
  background: var(--df-bg);
  border: 1px solid var(--df-border-soft);
  border-radius: 12px;
  width: 100%;
  height: 100%;
  max-width: 1200px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0,0,0,0.32);
}
.df-player-header {
  background: var(--df-bg);
  color: var(--df-text);
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--df-border-soft);
}
.df-player-title {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.02em;
  flex: 1;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--df-text-secondary);
}
.df-player-close {
  background: transparent;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--df-placeholder);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 100ms ease, color 100ms ease;
}
.df-player-close:hover { background: var(--df-bg-soft); color: var(--df-text); }
.df-player-close svg { display: block; }

.df-player-download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: var(--df-bg);
  border: 1px solid var(--df-border-soft);
  border-radius: 6px;
  color: var(--df-text-secondary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
}
.df-player-download:hover { background: var(--df-bg-soft); color: var(--df-text); border-color: var(--df-border); }
.df-player-download svg { display: block; }

.df-player-csp-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: rgba(255,255,255,0.78);
  font-size: 13px;
  line-height: 1.55;
  text-align: center;
  padding: 32px 24px;
}
.df-player-csp-dl {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: var(--df-color);
  color: var(--df-color-fg);
  border: 1px solid var(--df-color);
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: filter 100ms ease;
}
.df-player-csp-dl:hover { filter: brightness(0.95); }
.df-player-csp-dl svg { display: block; }

.df-player-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0A0A0A;
  overflow: hidden;
  padding: 0;
}
.df-player-video {
  display: block;
  max-width: 100%;
  max-height: 100%;
  background: #0A0A0A;
}

@media (max-width: 600px) {
  .df-player-overlay { padding: 0; }
  .df-player-frame { border: 0; border-radius: 0; max-height: 100%; }
}

.df-recording-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--df-error);
  animation: df-pulse 1s ease-in-out infinite;
}

@keyframes df-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes df-rise { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: none; } }
@keyframes df-sheet { from { transform: translateY(100%); } to { transform: none; } }
@keyframes df-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }

/* Phone: a bottom sheet with a grab handle (fits 375 x 740 empty). */
@media (max-width: 480px) {
  .df-launcher { padding: 12px; }
  .df-launcher .df-label-text { display: none; }
  .df-backdrop,
  .df-backdrop.df-at-bottom-right, .df-backdrop.df-at-bottom-left,
  .df-backdrop.df-at-top-right, .df-backdrop.df-at-top-left {
    padding: 0;
    align-items: flex-end;
    justify-content: stretch;
    background: rgba(10, 10, 10, 0.28);
  }
  .df-modal {
    width: 100%;
    max-width: none;
    border-radius: 16px 16px 0 0;
    border-bottom: 0;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    /* The visible viewport while the keyboard is up (set by trackViewport). */
    max-height: calc(var(--df-vvh, 100dvh) - 24px);
    animation: df-sheet 220ms cubic-bezier(.2,.7,.3,1);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .df-grab { display: block; }
  .df-send-row { flex-direction: column; align-items: stretch; }
  .df-send-row .df-send { width: 100%; }
  .df-foot-long { display: none; }
  .df-foot-short { display: inline; }
  .df-body { min-height: 48px; }
}

@media (prefers-reduced-motion: reduce) {
  .df-backdrop, .df-modal, .df-launcher, .df-recording-dot, .df-rec-dot, .df-bars b { animation: none; transition: none; }
}
`;
